import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, Session } from '@kontor/shared';
import { api, ApiError } from './api';

interface SessionContextValue {
  session: Session | null;
  loading: boolean;
  /** Fehler beim Laden der Sitzung, der nicht "nicht angemeldet" ist, z. B. gesperrter Mandant */
  error: ApiError | null;
  can: (permission: string) => boolean;
  hasModule: (module: string) => boolean;
  /** Hat die Branche diesen Bereich aus dem Menü ausgeblendet (Schlüssel wie im Menü, zum Beispiel „/lager“)? Seiten verlinken ausgeblendete Bereiche nicht. */
  areaHidden: (area: string) => boolean;
  login: (input: Omit<LoginInput, 'mode'>) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const me = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return (await api<{ data: Session }>('GET', '/auth/me')).data;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: Omit<LoginInput, 'mode'>) => api<{ data: Session }>('POST', '/auth/login', { ...input, mode: 'cookie', deviceName: 'Backoffice' }),
    onSuccess: (res) => client.setQueryData(['session'], res.data),
  });

  const session = me.data ?? null;
  const value: SessionContextValue = {
    session,
    loading: me.isLoading,
    error: me.error instanceof ApiError ? me.error : null,
    can: (p) => session?.user.permissions.includes(p) ?? false,
    hasModule: (m) => session?.tenant.modules.includes(m) ?? false,
    areaHidden: (a) => session?.tenant.hiddenAreas?.includes(a) ?? false,
    login: async (input) => {
      await loginMutation.mutateAsync(input);
    },
    logout: async () => {
      try {
        await api('POST', '/auth/logout');
      } finally {
        // Alle Daten des bisherigen Benutzers verwerfen. Die Abfrage der Sitzung bleibt bestehen: Sie ist im
        // SessionProvider eingehängt und würde den neuen Wert nach einem client.clear() nicht mehr bekommen,
        // die Oberfläche zeigte dann weiter den alten Benutzer.
        client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
        client.setQueryData(['session'], null);
      }
    },
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession außerhalb von SessionProvider');
  return ctx;
}
